import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { TransitionHookCreateBody, TransitionHookUpdateBody } from '@/domain/workflow-admin.types'
import { workflowAdminApi } from '@/services/workflow.service'

/** React Query hooks for the builder's per-transition hook CRUD (`transition_hook`). */

const ROOT = 'transition-hook-admin'

export const transitionHookAdminKeys = {
	forTransition: (transitionId: string | null) => [ROOT, transitionId] as const,
}

export const useTransitionHooks = (transitionId: string | null) =>
	useQuery({
		queryKey: transitionHookAdminKeys.forTransition(transitionId),
		queryFn: () => workflowAdminApi.listTransitionHooks(transitionId as string),
		enabled: !!transitionId,
	})

export const useCreateTransitionHook = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: TransitionHookCreateBody) => workflowAdminApi.createTransitionHook(body),
		onSuccess: (_data, body) =>
			void qc.invalidateQueries({ queryKey: transitionHookAdminKeys.forTransition(body.transitionId) }),
	})
}

export const useUpdateTransitionHook = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ hookId, body }: { hookId: string; transitionId: string; body: TransitionHookUpdateBody }) =>
			workflowAdminApi.updateTransitionHook(hookId, body),
		onSuccess: (_data, { transitionId }) =>
			void qc.invalidateQueries({ queryKey: transitionHookAdminKeys.forTransition(transitionId) }),
	})
}

export const useDeleteTransitionHook = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ hookId }: { hookId: string; transitionId: string }) => workflowAdminApi.deleteTransitionHook(hookId),
		onSuccess: (_data, { transitionId }) =>
			void qc.invalidateQueries({ queryKey: transitionHookAdminKeys.forTransition(transitionId) }),
	})
}
